import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import CONFIG from '../../../../config';

const useUsers = () => {
  const API_BASE_URL = CONFIG.API.BASE_URL;
  const getToken = CONFIG.getToken;

  // State
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [allAdmins, setAllAdmins] = useState([]);
  const [allRegularUsers, setAllRegularUsers] = useState([]);
  const [adminSuggestions, setAdminSuggestions] = useState([]);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  
  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [companySearch, setCompanySearch] = useState('');
  const [adminSearch, setAdminSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  
  // Modal States
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showAdminMappingModal, setShowAdminMappingModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Form States
  const [newUser, setNewUser] = useState({ 
    email: '', 
    password: '',
    role: 'user',
    is_active: true,
    company_ids: []
  });
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({
    email: '',
    role: 'user',
    is_active: true,
    company_ids: []
  });
  const [adminMappingData, setAdminMappingData] = useState({
    admin_id: '',
    user_ids: []
  });
  const [selectedUsersForMapping, setSelectedUsersForMapping] = useState([]);
  
  // Loading States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingAdminMapping, setLoadingAdminMapping] = useState(false);

  // Fetch all companies
  const fetchCompanies = useCallback(async (forceRefresh = false) => {
    if (companies.length > 0 && !forceRefresh) {
      return;
    }
    
    try {
      setLoadingCompanies(true);
      const response = await axios.get(CONFIG.getFullUrl(CONFIG.API.COMPANIES.BASE), {
        headers: CONFIG.getAuthHeaders()
      });
      
      setCompanies(response.data);
      setFilteredCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies.');
    } finally {
      setLoadingCompanies(false);
    }
  }, [companies.length]);

  // Fetch all users with their details
  const fetchAllUsers = useCallback(async (forceRefresh = false) => {
    try {
      const response = await axios.get(CONFIG.getFullUrl(CONFIG.API.USERS.BASE), {
        headers: CONFIG.getAuthHeaders()
      });
      
      const allUsers = response.data;
      
      // Separate admins and regular users
      const admins = allUsers.filter(user => 
        user.role === 'admin' || user.role === 'superadmin'
      );
      
      const regularUsers = allUsers.filter(user => 
        user.role === 'user'
      );
      
      // Create a map for faster lookup
      const userMap = {};
      allUsers.forEach(user => {
        userMap[user.id] = user;
      });
      
      // Process admins to find their managed users
      const adminsWithManagedUsers = admins.map(admin => {
        // Find users who have this admin as their manager
        // Assuming there's a manager_id field or we need to check relationships
        const managedUsers = allUsers.filter(user => {
          // Check for direct manager relationship
          if (user.manager_id === admin.id) return true;
          // Check for admin_id relationship
          if (user.admin_id === admin.id) return true;
          // Check if user is in admin's managed_users array (if backend provides it)
          if (admin.managed_user_ids && admin.managed_user_ids.includes(user.id)) return true;
          return false;
        });
        
        return {
          ...admin,
          managed_users: managedUsers || [],
          managed_user_ids: (managedUsers || []).map(u => u.id)
        };
      });
      
      setAllAdmins(adminsWithManagedUsers);
      setAllRegularUsers(regularUsers);
      setAdminSuggestions(adminsWithManagedUsers);
      setUserSuggestions(regularUsers);
      
      // Combine all users for the main table
      const allUsersCombined = [...adminsWithManagedUsers, ...regularUsers];
      
      // Fetch companies for each user
      const usersWithDetails = await Promise.all(
        allUsersCombined.map(async (user) => {
          try {
            let companiesResponse = { data: [] };
            if (user.role === 'superadmin') {
              companiesResponse.data = companies;
            } else if (user.role === 'admin') {
              companiesResponse = await axios.get(
                CONFIG.getFullUrl(CONFIG.API.USERS.ADMIN_COMPANIES(user.id)),
                {
                  headers: CONFIG.getAuthHeaders()
                }
              ).catch(() => ({ data: [] }));
            } else {
              companiesResponse = await axios.get(
                CONFIG.getFullUrl(CONFIG.API.USERS.USER_COMPANIES(user.id)),
                {
                  headers: CONFIG.getAuthHeaders()
                }
              ).catch(() => ({ data: [] }));
            }
            
            return {
              ...user,
              companies: companiesResponse.data || []
            };
          } catch (error) {
            console.log(`Error fetching companies for user ${user.email}:`, error);
            return {
              ...user,
              companies: []
            };
          }
        })
      );
      
      setUsers(usersWithDetails);
      
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  }, [companies]);

  // Initial data fetch
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await fetchCompanies();
      await fetchAllUsers();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [fetchCompanies, fetchAllUsers]);

  // Helper function to add user to suggestions immediately
  const addUserToSuggestions = (newUserData) => {
    if (newUserData.role === 'user') {
      setAllRegularUsers(prev => [...prev, newUserData]);
      setUserSuggestions(prev => [...prev, newUserData]);
    } else if (newUserData.role === 'admin' || newUserData.role === 'superadmin') {
      const adminData = {
        ...newUserData,
        managed_users: [],
        managed_user_ids: []
      };
      setAllAdmins(prev => [...prev, adminData]);
      setAdminSuggestions(prev => [...prev, adminData]);
    }
  };

  // Map companies to user
  const mapCompaniesToUser = async (userId, companyIds, role) => {
    let endpoint;
    let requestBody;
    
    if (role === 'admin') {
      endpoint = CONFIG.API.COMPANIES.MAP_TO_ADMIN;
      requestBody = {
        target_id: userId,
        company_ids: companyIds
      };
    } else if (role === 'user') {
      endpoint = CONFIG.API.COMPANIES.MAP_TO_USER;
      requestBody = {
        target_id: userId,
        company_ids: companyIds
      };
    } else {
      return true;
    }
    
    try {
      await axios.post(CONFIG.getFullUrl(endpoint), requestBody, {
        headers: CONFIG.getAuthHeaders()
      });
      return true;
    } catch (error) {
      console.error(`Error mapping companies for ${role}:`, error);
      throw error;
    }
  };

  // Handle admin to user mapping
  const handleAdminUserMapping = async () => {
    if (!adminMappingData.admin_id || adminMappingData.user_ids.length === 0) {
      toast.error('Please select an admin and at least one user');
      return;
    }

    setLoadingAdminMapping(true);
    try {
      await axios.post(
        CONFIG.getFullUrl(CONFIG.API.USERS.MAP_TO_ADMIN),
        {
          admin_id: parseInt(adminMappingData.admin_id),
          user_ids: adminMappingData.user_ids
        },
        {
          headers: CONFIG.getAuthHeaders()
        }
      );
      
      fetchData();
      
      setAdminMappingData({
        admin_id: '',
        user_ids: []
      });
      setSelectedUsersForMapping([]);
      setShowAdminMappingModal(false);
      
      toast.success('Users successfully mapped to admin');
    } catch (error) {
      console.error('Error mapping users to admin:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Failed to map users to admin');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error('Failed to map users to admin. Please try again.');
      }
    } finally {
      setLoadingAdminMapping(false);
    }
  };

  // Handle form submission for add user
  const handleAddSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!newUser.email.trim()) {
      toast.error('Email is required');
      return;
    }
    
    if (!newUser.password.trim() || newUser.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      const userData = {
        email: newUser.email.trim(),
        password: newUser.password,
        role: newUser.role,
        is_active: newUser.is_active
      };

      const response = await axios.post(CONFIG.getFullUrl(CONFIG.API.USERS.BASE), userData, {
        headers: CONFIG.getAuthHeaders()
      });
      
      const newUserId = response.data.id;
      
      const newUserObj = {
        id: newUserId,
        email: newUser.email.trim(),
        role: newUser.role,
        is_active: newUser.is_active,
        created_at: new Date().toISOString(),
        companies: [],
        managed_users: [],
        managed_user_ids: []
      };
      
      setUsers(prevUsers => [...prevUsers, newUserObj]);
      addUserToSuggestions(newUserObj);
      
      if (newUser.company_ids.length > 0 && newUser.role !== 'superadmin') {
        try {
          await mapCompaniesToUser(newUserId, newUser.company_ids, newUser.role);
          
          const userWithCompanies = {
            ...newUserObj,
            companies: companies.filter(company => newUser.company_ids.includes(company.id))
          };
          
          setUsers(prevUsers => 
            prevUsers.map(user => 
              user.id === newUserId ? userWithCompanies : user
            )
          );
          
        } catch (mappingError) {
          console.error('Company mapping failed:', mappingError);
          toast.warning('User created but company mapping failed');
        }
      }
      
      setNewUser({ 
        email: '', 
        password: '',
        role: 'user',
        is_active: true,
        company_ids: []
      });
      setShowAddModal(false);
      setCompanySearch('');
      toast.success('User created successfully');
      
    } catch (error) {
      console.error('Error creating user:', error.response || error);
      if (error.response?.status === 400) {
        if (error.response.data.detail?.includes('already exists')) {
          toast.error('User with this email already exists');
        } else {
          toast.error(error.response.data.detail || 'Validation error');
        }
      } else if (error.response?.status === 403) {
        toast.error('Permission denied: Cannot create Superadmin');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else if (error.response?.status === 422) {
        toast.error('Validation error: ' + JSON.stringify(error.response.data.detail));
      } else {
        toast.error('Failed to create user. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit user
  const handleEditSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!editingUser || !editUserData.email.trim()) {
      toast.error('Email is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData = {
        email: editUserData.email.trim(),
        role: editUserData.role,
        is_active: editUserData.is_active
      };

      const response = await axios.put(
        CONFIG.getFullUrl(`${CONFIG.API.USERS.BASE}/${editingUser.id}`),
        updateData,
        {
          headers: CONFIG.getAuthHeaders()
        }
      );
      
      const updatedUserObj = {
        ...response.data,
        companies: editingUser.companies || [],
        managed_users: editingUser.managed_users || [],
        managed_user_ids: editingUser.managed_user_ids || []
      };
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === editingUser.id ? updatedUserObj : user
        )
      );
      
      if (editingUser.role !== editUserData.role) {
        if (editingUser.role === 'user') {
          setAllRegularUsers(prev => prev.filter(u => u.id !== editingUser.id));
          setUserSuggestions(prev => prev.filter(u => u.id !== editingUser.id));
        } else {
          setAllAdmins(prev => prev.filter(u => u.id !== editingUser.id));
          setAdminSuggestions(prev => prev.filter(u => u.id !== editingUser.id));
        }
        
        if (editUserData.role === 'user') {
          setAllRegularUsers(prev => [...prev, updatedUserObj]);
          setUserSuggestions(prev => [...prev, updatedUserObj]);
        } else {
          const adminData = {
            ...updatedUserObj,
            managed_users: [],
            managed_user_ids: []
          };
          setAllAdmins(prev => [...prev, adminData]);
          setAdminSuggestions(prev => [...prev, adminData]);
        }
      } else {
        if (editUserData.role === 'user') {
          setAllRegularUsers(prev => 
            prev.map(user => 
              user.id === editingUser.id ? updatedUserObj : user
            )
          );
          setUserSuggestions(prev => 
            prev.map(user => 
              user.id === editingUser.id ? updatedUserObj : user
            )
          );
        } else {
          setAllAdmins(prev => 
            prev.map(user => 
              user.id === editingUser.id ? updatedUserObj : user
            )
          );
          setAdminSuggestions(prev => 
            prev.map(user => 
              user.id === editingUser.id ? updatedUserObj : user
            )
          );
        }
      }
      
      if (editUserData.role !== 'superadmin') {
        try {
          await mapCompaniesToUser(editingUser.id, editUserData.company_ids || [], editUserData.role);
          
          let companiesResponse;
          if (editUserData.role === 'admin') {
            companiesResponse = await axios.get(
              CONFIG.getFullUrl(CONFIG.API.USERS.ADMIN_COMPANIES(editingUser.id)),
              {
                headers: CONFIG.getAuthHeaders()
              }
            ).catch(() => ({ data: [] }));
          } else {
            companiesResponse = await axios.get(
              CONFIG.getFullUrl(CONFIG.API.USERS.USER_COMPANIES(editingUser.id)),
              {
                headers: CONFIG.getAuthHeaders()
              }
            ).catch(() => ({ data: [] }));
          }
          
          const updatedUserWithCompanies = {
            ...updatedUserObj,
            companies: companiesResponse.data || []
          };
          
          setUsers(prevUsers => 
            prevUsers.map(user => 
              user.id === editingUser.id ? updatedUserWithCompanies : user
            )
          );
          
        } catch (mappingError) {
          console.error('Error updating company mappings:', mappingError);
          toast.warning('User updated but company mapping failed');
        }
      } else {
        setUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === editingUser.id ? {
              ...updatedUserObj,
              companies: companies
            } : user
          )
        );
      }
      
      setEditingUser(null);
      setShowEditModal(false);
      setCompanySearch('');
      toast.success('User updated successfully');
      
    } catch (error) {
      console.error('Error updating user:', error.response || error);
      
      if (error.response?.status === 404) {
        toast.error('User not found');
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Validation error');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update user. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    const userToDelete = users.find(u => u.id === userId);
    
    try {
      await axios.delete(CONFIG.getFullUrl(`${CONFIG.API.USERS.BASE}/${userId}`), {
        headers: CONFIG.getAuthHeaders()
      });
      
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      
      if (userToDelete.role === 'user') {
        setAllRegularUsers(prev => prev.filter(user => user.id !== userId));
        setUserSuggestions(prev => prev.filter(user => user.id !== userId));
      } else {
        setAllAdmins(prev => prev.filter(user => user.id !== userId));
        setAdminSuggestions(prev => prev.filter(user => user.id !== userId));
      }
      
      setShowDeleteConfirm(null);
      toast.success('User deleted successfully');
      
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error.response?.status === 404) {
        toast.error('User not found');
      } else if (error.response?.status === 400) {
        toast.error('Cannot delete the last Superadmin');
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to delete user. Please try again.');
      }
    }
  };

  // Open edit modal
  const openEditModal = (user) => {
    setEditingUser(user);
    setEditUserData({
      email: user.email,
      role: user.role,
      is_active: user.is_active,
      company_ids: user.companies?.map(c => c.id) || []
    });
    setShowEditModal(true);
  };

  // Open admin mapping modal
  const openAdminMappingModal = () => {
    setShowAdminMappingModal(true);
    setAdminSearch('');
    setUserSearch('');
  };

  // Handle company checkbox change
  const handleCompanyCheckboxChange = (companyId, isEdit = false) => {
    if (isEdit) {
      setEditUserData(prev => {
        if (prev.company_ids.includes(companyId)) {
          return {
            ...prev,
            company_ids: prev.company_ids.filter(id => id !== companyId)
          };
        } else {
          return {
            ...prev,
            company_ids: [...prev.company_ids, companyId]
          };
        }
      });
    } else {
      setNewUser(prev => {
        if (prev.company_ids.includes(companyId)) {
          return {
            ...prev,
            company_ids: prev.company_ids.filter(id => id !== companyId)
          };
        } else {
          return {
            ...prev,
            company_ids: [...prev.company_ids, companyId]
          };
        }
      });
    }
  };

  // Handle user checkbox change for admin mapping
  const handleUserCheckboxForAdminMapping = (userId) => {
    setSelectedUsersForMapping(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
    
    setAdminMappingData(prev => ({
      ...prev,
      user_ids: prev.user_ids.includes(userId) 
        ? prev.user_ids.filter(id => id !== userId)
        : [...prev.user_ids, userId]
    }));
  };

  // Filter admin suggestions based on search
  const filterAdminSuggestions = (search = '') => {
    if (!search.trim()) {
      setAdminSuggestions(allAdmins);
      return;
    }
    
    const filtered = allAdmins.filter(admin =>
      admin.email.toLowerCase().includes(search.toLowerCase())
    );
    setAdminSuggestions(filtered);
  };

  // Filter user suggestions based on search
  const filterUserSuggestions = (search = '') => {
    if (!search.trim()) {
      setUserSuggestions(allRegularUsers);
      return;
    }
    
    const filtered = allRegularUsers.filter(user =>
      user.email.toLowerCase().includes(search.toLowerCase())
    );
    setUserSuggestions(filtered);
  };

  // Filter companies based on search
  useEffect(() => {
    if (companySearch.trim() === '') {
      setFilteredCompanies(companies);
    } else {
      const filtered = companies.filter(company =>
        company.name.toLowerCase().includes(companySearch.toLowerCase()) ||
        (company.ticker_id && company.ticker_id.toLowerCase().includes(companySearch.toLowerCase()))
      );
      setFilteredCompanies(filtered);
    }
  }, [companySearch, companies]);

  // Filter admin suggestions when search changes
  useEffect(() => {
    filterAdminSuggestions(adminSearch);
  }, [adminSearch, allAdmins]);

  // Filter user suggestions when search changes
  useEffect(() => {
    filterUserSuggestions(userSearch);
  }, [userSearch, allRegularUsers]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Get company names for display
  const getCompanyNames = (userCompanies) => {
    if (!userCompanies || userCompanies.length === 0) {
      return 'None';
    }
    return userCompanies.map(c => c.name).join(', ');
  };

  // Get managed user names for display
  const getManagedUserNames = (user) => {
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      return 'N/A';
    }
    
    if (!user.managed_users || user.managed_users.length === 0) {
      return 'None';
    }
    
    return user.managed_users.map(u => u.email).join(', ');
  };

  // Get managed user count for display
  const getManagedUserCount = (user) => {
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      return '-';
    }
    return user.managed_users?.length || 0;
  };

  // Refresh all data
  const handleRefresh = () => {
    fetchData();
  };

  // Handle opening Add User modal
  const handleOpenAddModal = () => {
    setNewUser({ 
      email: '', 
      password: '',
      role: 'user',
      is_active: true,
      company_ids: []
    });
    setCompanySearch('');
    setShowAddModal(true);
  };

  return {
    // State
    users,
    companies,
    loading,
    loadingCompanies,
    allAdmins,
    allRegularUsers,
    adminSuggestions,
    userSuggestions,
    filteredCompanies,
    
    // Search & Filter States
    searchTerm,
    setSearchTerm,
    roleFilter,
    setRoleFilter,
    companySearch,
    setCompanySearch,
    adminSearch,
    setAdminSearch,
    userSearch,
    setUserSearch,
    
    // Modal States
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    showDeleteConfirm,
    setShowDeleteConfirm,
    showAdminMappingModal,
    setShowAdminMappingModal,
    showPassword,
    setShowPassword,
    
    // Form States
    newUser,
    setNewUser,
    editingUser,
    setEditingUser,
    editUserData,
    setEditUserData,
    adminMappingData,
    setAdminMappingData,
    selectedUsersForMapping,
    setSelectedUsersForMapping,
    
    // Loading States
    isSubmitting,
    setIsSubmitting,
    loadingAdminMapping,
    setLoadingAdminMapping,
    
    // Functions
    fetchData,
    handleRefresh,
    handleAddSubmit,
    handleEditSubmit,
    handleDeleteUser,
    handleAdminUserMapping,
    filterAdminSuggestions,
    filterUserSuggestions,
    handleCompanyCheckboxChange,
    handleUserCheckboxForAdminMapping,
    openEditModal,
    openAdminMappingModal,
    getCompanyNames,
    getManagedUserNames,
    getManagedUserCount,
    handleOpenAddModal,
    getToken,
    API_BASE_URL
  };
};

export default useUsers;